import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Brightness Meter (renamed from Lux Meter per pushback). Phone cameras
// don't expose true lux; we'd have to guess scene reflectivity. Instead
// we report:
//   1. A labeled zone (Dim / Indoor / Bright / Direct sun)
//   2. EV (exposure value) as the underlying number — honest unit, what
//      the camera actually measures.
//
// We sample the average brightness of the camera image directly. Most
// browsers don't expose ISO/shutter/aperture, but the rendered pixel
// brightness is a usable proxy once normalized.

const ZONES = [
  { max: 30,  label: "Very dim",      hint: "moonlight, deep dusk",        color: "#5B6BD9" },
  { max: 80,  label: "Dim",           hint: "candle-lit, twilight",         color: "#7C5BD9" },
  { max: 130, label: "Indoor",        hint: "typical living room",          color: "#D97B9A" },
  { max: 180, label: "Bright indoor", hint: "office, kitchen task lighting", color: "#E0584E" },
  { max: 220, label: "Bright",        hint: "overcast outdoors, near window", color: "#F8B84E" },
  { max: 256, label: "Direct sun",    hint: "outdoor sunlight",             color: "#E5C13A" },
];

const ZONE_FOR = (avgBrightness) => {
  for (const z of ZONES) if (avgBrightness <= z.max) return z;
  return ZONES[ZONES.length - 1];
};

export default function BrightnessMeter() {
  const [state, setState] = useState("idle");
  const [avg, setAvg] = useState(0);
  const [history, setHistory] = useState([]);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const enable = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { setState("unsupported"); return; }
    if (!window.isSecureContext) { setState("insecure"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      // V57.6: srcObject attachment moved to post-render useEffect.
      // The <video> element is conditionally rendered (state === "ok")
      // and isn't in the DOM at this point — assigning srcObject here
      // would silently no-op on a null ref.
      setState("ok");
      requestAnimationFrame(sample);
    } catch (err) {
      // V57.5: differentiate error types so the UI is honest
      if (err && err.name === "NotAllowedError") setState("denied");
      else if (err && err.name === "NotFoundError") setState("nodevices");
      else setState("denied");
    }
  };

  const sample = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || v.readyState < 2 || !v.videoWidth) {
      rafRef.current = requestAnimationFrame(sample);
      return;
    }
    // Down-sample to 32×32 to make analysis fast
    c.width = 32; c.height = 32;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(v, 0, 0, 32, 32);
    const data = ctx.getImageData(0, 0, 32, 32).data;
    let sum = 0;
    for (let i = 0; i < data.length; i += 4) {
      // Standard luminance weighting
      sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    const a = sum / (data.length / 4);
    setAvg(a);
    setHistory((h) => [...h.slice(-99), a]);
    rafRef.current = setTimeout(() => requestAnimationFrame(sample), 100);
  };

  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (rafRef.current) {
      if (typeof rafRef.current === "number") cancelAnimationFrame(rafRef.current);
      else clearTimeout(rafRef.current);
    }
  }, []);

  // V57.6: attach stream after the <video> element renders.
  useEffect(() => {
    if (state === "ok" && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [state]);

  const zone = ZONE_FOR(avg);
  // "EV-like" reading: log2(brightness / reference). Reference at avg=128
  // (mid-gray) calls that EV 0. Cap range to ±5.
  const evLike = avg > 0 ? Math.max(-5, Math.min(5, Math.log2(avg / 18))) : -5;

  return (
    <ToolsShell title="Brightness Meter">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Approximate ambient brightness from camera input. Honest about precision: this is a labeled zone (Dim / Indoor / Bright / Direct sun) plus an EV-like value — not calibrated lux.
          </p>
          <button onClick={enable} style={primaryBtn}>Enable camera</button>
          <SensorNotice state={state === "idle" ? "prompt" : state} what="Camera" />
        </div>
      )}

      {state === "ok" && (
        <>
          {/* V57.2: opacity:0 instead of display:none — see SymmetryChecker comment.
              The canvas can stay display:none because it's never user-visible,
              but the video must render for iOS Safari to keep playing it. */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none", left: 0, top: 0 }}
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />

          {/* Zone display */}
          <div style={{ background: "#13141F", border: `1px solid ${zone.color}55`, borderRadius: 14, padding: "22px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Brightness zone</div>
            <div style={{ fontSize: 30, fontWeight: 800, color: zone.color, lineHeight: 1.1 }}>
              {zone.label}
            </div>
            <div style={{ fontSize: 13, color: "#9A9DBE", marginTop: 6 }}>{zone.hint}</div>
          </div>

          {/* Zone scale */}
          <div style={{ marginTop: 14, display: "flex", gap: 2, height: 30, borderRadius: 6, overflow: "hidden" }}>
            {ZONES.map((z, i) => (
              <div key={i} style={{
                flex: 1,
                background: z.color,
                opacity: z === zone ? 1 : 0.35,
                transition: "opacity 0.2s",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 9, fontWeight: 700, color: "#0A0B14",
              }}>
                {z === zone && "●"}
              </div>
            ))}
          </div>

          <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Card label="EV (relative)" value={`${evLike.toFixed(1)}`} />
            <Card label="Avg brightness" value={`${Math.round(avg)} / 255`} />
          </div>

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14 }}>
            <strong>Not a calibrated light meter.</strong> Phone cameras adjust exposure automatically, which biases the reading. For accurate lux measurements (worksite OSHA compliance, photography, plant growing), use a dedicated lux meter.
          </p>
        </>
      )}
    </ToolsShell>
  );
}

function Card({ label, value }) {
  return (
    <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}
